/**
 * HelpdeskTicketsInline Component
 *
 * Displays helpdesk ticket count with hover tooltip showing ticket details.
 * Uses intersection observer to only fetch tickets for visible rows.
 * Clicking navigates to helpdesk page with building filter applied.
 */

import { useState, useEffect, useRef } from "react";
import { Spinner, Tooltip, Chip } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { BuildingDTO } from "shared/types/building";
import { HelpdeskTicketInterface } from "shared/types/helpdesk";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Circle, LifebuoyIcon } from "@phosphor-icons/react";

interface HelpdeskTicketsInlineProps {
  building: BuildingDTO;
}

/**
 * Get priority color for ticket
 */
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'URGENT':
      return 'text-red-600';
    case 'HIGH':
      return 'text-orange-600';
    case 'MEDIUM':
      return 'text-yellow-600';
    case 'LOW':
      return 'text-green-600';
    default:
      return 'text-neutral-secondary-mid';
  }
};

/**
 * Get status badge color
 */
const getStatusColor = (status: string) => {
  switch (status) {
    case 'TODO':
      return 'bg-blue-100 text-blue-800';
    case 'IN_PROGRESS':
      return 'bg-yellow-100 text-yellow-800';
    case 'WAITING':
      return 'bg-orange-100 text-orange-800';
    case 'DONE':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-neutral-100 text-neutral-800';
  }
};

/**
 * Format status for display
 */
const formatStatus = (status: string) => {
  const statusMap: Record<string, string> = {
    'TODO': 'Te doen',
    'IN_PROGRESS': 'Bezig',
    'WAITING': 'Wachten',
    'DONE': 'Klaar'
  };
  return statusMap[status] || status;
};

/**
 * Tooltip content showing ticket details
 */
const TicketsTooltipContent = ({ tickets }: { tickets: HelpdeskTicketInterface[] }) => {
  if (tickets.length === 0) {
    return (
      <div className="p-2">
        <p className="text-xs text-neutral-secondary-mid">Geen tickets voor dit gebouw</p>
      </div>
    );
  }

  return (
    <div className="p-2 max-w-xl max-h-80 overflow-y-auto">
      <p className="font-medium mb-2 sticky top-0 bg-inherit">
        Helpdesk Tickets ({tickets.length})
      </p>
      <div className="space-y-2">
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            className="border-l-2 border-moss-primary/30 pl-2 py-1 hover:bg-neutral-50/50 rounded-sm"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-neutral-primary">
                  H{ticket.id}
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded-sm ${getStatusColor(ticket.internalStatus)}`}>
                  {formatStatus(ticket.internalStatus)}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Circle weight="fill" size={8} className={getPriorityColor(ticket.priority)} />
                <span className={`text-xs ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority}
                </span>
              </div>
            </div>
            {ticket.label && (
              <p className="text-xs text-moss-primary mb-1">
                {ticket.label}
              </p>
            )}
            {ticket.title && (
              <p className="text-xs text-neutral-secondary-mid line-clamp-2">
                {ticket.title}
              </p>
            )}
            {ticket.unit?.id && (
              <p className="text-xs text-neutral-secondary-mid mt-1">
                📍 {ticket.unit.id}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-neutral-200 sticky bottom-0 bg-inherit">
        <p className="text-xs text-neutral-secondary-mid italic">
          Klik om naar helpdesk te gaan met filter
        </p>
      </div>
    </div>
  );
};

export const HelpdeskTicketsInline = ({ building }: HelpdeskTicketsInlineProps) => {
  const navigate = useNavigate();
  const { session } = useSession();
  const [isVisible, setIsVisible] = useState(false);
  const [tickets, setTickets] = useState<HelpdeskTicketInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  // Only fetch tickets when row becomes visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px', // Start loading 200px before visible
        threshold: 0.01
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Fetch tickets when visible
  useEffect(() => {
    const fetchTickets = async () => {
      if (!isVisible || !session || !building.id) return;

      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          status: 'open,completion',
          includeEmailCounts: 'true',
          building: building.id,
          pageSize: '9999',
          sortField: 'id',
          sortDirection: 'DESC',
        });
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/tickets?${params}`,
          { method: 'GET' },
          session
        );

        if (response.ok) {
          const result = await response.json();
          setTickets(result.data ?? []);
        }
      } catch (error) {
        console.error('Error fetching tickets for building:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTickets();
  }, [isVisible, session, building.id]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to helpdesk with building filter
    navigate(`/helpdesk?building=${building.id}`);
  };

  const ticketCount = building.ticketCount ?? 0;

  // Don't render anything if not visible yet (saves performance)
  if (!isVisible) {
    return (
      <div ref={elementRef}>
        <Chip
          startContent={<LifebuoyIcon weight="duotone" size={14} />}
          classNames={{
            base: "h-fit bg-muted gap-1 px-2 border border-muted-foreground/50 text-muted-foreground",
            content: "px-0 font-medium text-muted-foreground",
          }}
        >
          {ticketCount}
        </Chip>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-1">
        <Spinner size="sm" />
        <span className="text-xs text-muted-foreground">Laden...</span>
      </div>
    );
  }

  return (
    <Tooltip
      content={<TicketsTooltipContent tickets={tickets} />}
      placement="top"
    >
      <div onClick={handleClick} className="cursor-pointer inline-flex h-fit">
        <Chip
          startContent={<LifebuoyIcon weight="duotone" size={14} />}
          classNames={{
            base: "h-fit gap-1 bg-muted px-2 border border-muted-foreground/50 text-muted-foreground hover:border-moss-primary hover:text-moss-primary transition-colors",
            content: "px-0 font-medium text-muted-foreground",
          }}
        >
          {ticketCount}
        </Chip>
      </div>
    </Tooltip>
  );
};
